import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import Button from '../../../components/ui/Button';
import GenericSelect from '../../../components/ui/GenericSelect';
import AsyncGenericSelect from '../../../components/ui/AsyncGenericSelect';
import DateTimePicker from '../../../components/ui/DateTimePicker';
import PersonCell from '../../../components/ui/PersonCell';
import api from '../../../services/api';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 42, 42, 0.4);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: ${fadeIn} 0.15s ease;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 6px;
  width: 440px;
  max-width: 90vw;
  box-shadow: 0 16px 48px rgba(0, 66, 66, 0.15);
  animation: ${slideUp} 0.2s ease;
  font-family: 'Nunito Sans', sans-serif;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 16px;
  border-bottom: 1px solid #E8ECF0;
`;

const ModalTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #004242;
  margin: 0;
`;

const CloseBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 4px;
  background: transparent;
  cursor: pointer;
  color: #A0A8B4;
  &:hover { background: rgba(0, 0, 0, 0.04); color: #004242; }
`;

const ModalBody = styled.div`
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px 20px;
  border-top: 1px solid #E8ECF0;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #6B7280;
  margin-bottom: 6px;
`;

const TextInput = styled.input`
  width: 100%;
  height: 48px;
  padding: 0 14px;
  border: 1.5px solid #E0E4E8;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  color: #004242;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.2s;
  &:focus { border-color: #0291EF; }
  &::placeholder { color: #A0A8B4; }
`;

const assignmentTypeOptions = [
  { id: 'primary', name: 'primary' },
  { id: 'secondary', name: 'secondary' },
  { id: 'proxy', name: 'proxy' },
];

export default function AssignModal({ isOpen, positionId, onClose, onSuccess }) {
  const { t } = useTranslation();
  const [userId, setUserId] = useState(null);
  const [assignmentType, setAssignmentType] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [changeReason, setChangeReason] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setUserId(null);
      setAssignmentType(null);
      setEndDate(null);
      setChangeReason('');
    }
  }, [isOpen]);

  const searchEmployees = useCallback(async (query) => {
    try {
      const params = { pageSize: 20 };
      if (query) params['firstName[like]'] = query;
      const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
      const result = res.data?.data;
      const items = result?.data || result || [];
      return Array.isArray(items)
        ? items.map((emp) => ({
            id: emp.id,
            name: `${emp.firstName || ''} ${emp.lastName || ''}`.trim(),
            profilePicture: emp.profilePicture,
            firstName: emp.firstName,
            lastName: emp.lastName,
          }))
        : [];
    } catch {
      return [];
    }
  }, []);

  const getEmployeeById = useCallback(async (id) => {
    try {
      const res = await api.get(`/v1/employees/${id}`);
      const emp = res.data?.data;
      if (!emp) return null;
      return {
        id: emp.id,
        name: `${emp.firstName || ''} ${emp.lastName || ''}`.trim(),
        profilePicture: emp.profilePicture,
        firstName: emp.firstName,
        lastName: emp.lastName,
      };
    } catch {
      return null;
    }
  }, []);

  const handleSubmit = async () => {
    if (!userId || !assignmentType) return;
    setLoading(true);
    try {
      await api.post('/v1/user-positions', {
        userId,
        positionId,
        startDate: new Date().toISOString(),
        assignmentType,
        endDate: assignmentType === 'proxy' ? endDate : null,
        changeReason: changeReason || undefined,
      });
      onSuccess?.();
      onClose();
    } catch {
      // toast handled by interceptor
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const typeLabels = assignmentTypeOptions.map((opt) => ({
    ...opt,
    name: t(`orgChart.${opt.id}`),
  }));

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>{t('orgChart.assign')}</ModalTitle>
          <CloseBtn onClick={onClose}><X size={18} /></CloseBtn>
        </ModalHeader>
        <ModalBody>
          <div>
            <AsyncGenericSelect
              label={t('orgChart.selectUser')}
              value={userId}
              onChange={setUserId}
              onSearch={searchEmployees}
              onGetById={getEmployeeById}
              renderOption={(opt) => (
                <PersonCell name={opt.name} profilePicture={opt.profilePicture} />
              )}
              required
            />
          </div>
          <div>
            <GenericSelect
              label={t('orgChart.assignmentType')}
              options={typeLabels}
              value={assignmentType}
              onChange={setAssignmentType}
              required
            />
          </div>
          {assignmentType === 'proxy' && (
            <div>
              <DateTimePicker
                label={t('resume.endDate')}
                value={endDate}
                onChange={setEndDate}
                mode="datetime"
              />
            </div>
          )}
          <div>
            <FieldLabel>{t('orgChart.changeReason')}</FieldLabel>
            <TextInput
              value={changeReason}
              onChange={(e) => setChangeReason(e.target.value)}
              placeholder={t('orgChart.changeReason')}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={loading || !userId || !assignmentType}>
            {loading ? '...' : t('common.save')}
          </Button>
        </ModalFooter>
      </Modal>
    </Overlay>
  );
}
